'use server'

import { createClient } from '@/lib/supabase/server'
import { effectiveTier } from '@/lib/ai-usage'
import { learnerBotConfigured, isTgOfferDue } from '@/lib/learner-bot'
import type { ActivityResult } from '@/lib/activity'
import { isShortWord } from '@/lib/srs'
import type { LessonPart } from '@/lib/lessons/parts'

export type AlreadySaved = { already_saved: true }
// feedback_hint: ровно третий пройденный урок курса и строка о награде за
// предложения (тикет 96) ещё не показывалась; плеер показывает её один раз.
// tg_offer: окно "Слова в Telegram" (16.10.2026, повтор каждые несколько
// уроков с 19.10), см. tgOfferDue и lib/learner-bot.ts (isTgOfferDue). count
// нужен клиенту, чтобы отметить, на каком уроке окно показали (mark_tg_offer_shown).
export type TgOffer = { paid: boolean; count: number }
export type PartResult = (ActivityResult & { parts_done?: number; lesson_xp?: number; feedback_hint?: boolean; tg_offer?: TgOffer }) | AlreadySaved

import { FEEDBACK_HINT_KEY } from '@/lib/onboarding'

// Третий пройденный урок основного курса (треки travel и life не в счёт) и
// отметки hint_feedback_reward в профиле нет. Два лёгких запроса только для
// части 3; любая ошибка здесь строку не показывает и урок не ломает.
async function courseLessonsDone(supabase: Awaited<ReturnType<typeof createClient>>, userId: string): Promise<number | null> {
  const { count, error } = await supabase
    .from('user_lesson_progress')
    .select('lesson_id, lessons!inner(track)', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('lessons.track', 'course')
    .not('completed_at', 'is', null)
  return error ? null : count
}

async function feedbackHintDue(supabase: Awaited<ReturnType<typeof createClient>>, userId: string, count: number | null): Promise<boolean> {
  if (count !== 3) return false
  const { data: profile } = await supabase.from('profiles').select('onboarding').eq('id', userId).maybeSingle()
  const ob = (profile?.onboarding ?? {}) as Record<string, boolean | undefined>
  return !ob[FEEDBACK_HINT_KEY]
}

// Окно "Слова в Telegram" (решение владельца 16.10.2026: до бота почти никто
// не доходил, он был только в настройках; повтор каждые несколько уроков с
// 19.10, см. isTgOfferDue). Ошибка здесь окно не показывает и урок не ломает.
async function tgOfferDue(supabase: Awaited<ReturnType<typeof createClient>>, userId: string, count: number | null): Promise<TgOffer | null> {
  if (count === null || !learnerBotConfigured()) return null
  const [{ data: profile, error }, { data: link }] = await Promise.all([
    supabase.from('profiles').select('tg_offer_last_count, subscription_status, subscription_until, plus_until, pro_until').eq('id', userId).maybeSingle(),
    supabase.from('tg_links').select('user_id').eq('user_id', userId).maybeSingle(),
  ])
  if (error || !profile || link) return null
  if (!isTgOfferDue(profile.tg_offer_last_count as number | null, count)) return null
  return { paid: effectiveTier(profile) !== 'free', count }
}

// В повторение идут только слова и пары слов (isShortWord): без букв
// алфавита, выражений и фраз треков «к отпуску» и «для жизни».
// Если словарь не прочитался, слова урока отдаются как есть: лучше лишняя
// карточка, чем урок, не давший слов в повторение.
async function srsSlugs(supabase: Awaited<ReturnType<typeof createClient>>, vocabIds: string[]): Promise<string[]> {
  if (!vocabIds.length) return []
  const { data: words, error } = await supabase.from('vocabulary').select('slug, turkish, grammar_tag').in('slug', vocabIds)
  if (error) return vocabIds
  return (words ?? []).filter((w) => w.grammar_tag !== 'alphabet' && isShortWord(w.turkish)).map((w) => w.slug)
}

/**
 * Часть урока закрыта (.scratch/lesson-parts/plan.md). Часть 1 кладёт слова в
 * SRS (ошибки проверки через 4 часа, остальные завтра), части 1 и 2 дают 5 XP
 * при первом прохождении, часть 3 завершает урок (XP добивает до 15, повтор 5).
 * Вся логика в security definer-функции complete_lesson_part (миграция
 * 20261008130000_lesson_parts.sql): клиент с обычным ключом не трогает profiles.xp.
 *
 * since: ISO-время первой попытки при повторе из офлайн-очереди (lib/lesson-sync.ts).
 * Для части 3 урок, записанный после since, не записываем второй раз (иначе
 * лишний «повтор» с опытом); для частей 1 и 2 повтор отсекает сама функция
 * (отвечает already_saved).
 *
 * guest: перенос урока, пройденного без регистрации (/guest-sync,
 * lib/guest-progress.ts): опыт и акче в профиль, без лиги и серии (миграция
 * 20261017200000_guest_import.sql), без строк о наградах и окна Telegram.
 */
export async function completeLessonPart(
  lessonId: string,
  part: LessonPart,
  vocabIds: string[],
  missedIds: string[],
  timezone: string,
  seconds: number,
  score: number | null = null,
  since: string | null = null,
  guest = false,
): Promise<PartResult> {
  const supabase = await createClient()
  const { data: claimsData, error: claimsError } = await supabase.auth.getClaims()

  if (claimsError || !claimsData?.claims) {
    throw new Error('Unauthorized')
  }

  if (part === 3 && since && !Number.isNaN(Date.parse(since))) {
    const { data: prev } = await supabase
      .from('user_lesson_progress')
      .select('completed_at')
      .eq('user_id', claimsData.claims.sub)
      .eq('lesson_id', lessonId)
      .maybeSingle()
    if (prev?.completed_at && Date.parse(prev.completed_at) >= Date.parse(since)) return { already_saved: true }
  }

  // Слова нужны части 1 (зачисление в SRS) и части 3 (страховка для пути без
  // частей: офлайн-очередь, пролистывание владельцем).
  const srsIds = part === 2 ? [] : await srsSlugs(supabase, vocabIds)

  const { data, error } = await supabase.rpc('complete_lesson_part', {
    p_lesson_id: lessonId,
    p_part: part,
    p_vocab_slugs: srsIds,
    p_missed_slugs: missedIds,
    p_timezone: timezone,
    p_seconds: seconds,
    p_score: score,
    ...(guest ? { p_guest: true } : {}),
  })

  if (error) {
    throw new Error(error.message)
  }

  const result = data as PartResult
  if (part === 3 && !guest && !('already_saved' in result)) {
    const userId = claimsData.claims.sub
    const count = await courseLessonsDone(supabase, userId)
    // Строка о награде и окно Telegram не в одном уроке: строка важнее, окно
    // подождёт следующего.
    if (await feedbackHintDue(supabase, userId, count)) return { ...result, feedback_hint: true }
    const tgOffer = await tgOfferDue(supabase, userId, count)
    if (tgOffer) return { ...result, tg_offer: tgOffer }
  }
  return result
}

// Прежний вход «урок целиком» (часть 3): его зовёт офлайн-очередь старых
// записей и тесты. Внутри та же complete_lesson_part(3).
export async function completeLesson(
  lessonId: string,
  score: number,
  vocabIds: string[],
  timezone: string,
  seconds: number,
  // Слова с ошибками на «Проверке» — придут на повторение через 4 часа, остальные завтра.
  missedIds: string[] = [],
  since: string | null = null,
): Promise<ActivityResult | AlreadySaved> {
  return completeLessonPart(lessonId, 3, vocabIds, missedIds, timezone, seconds, score, since)
}
